import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FC } from 'react';
import { Box, Checkbox, TableRow } from '@mui/material';
import { StyledTableCell } from 'ui/StyledTable';
import { nanoid } from '@reduxjs/toolkit';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { TTableStructure } from 'services/constants/tableStructureTypes';
import { TFilters, ESortDirection } from 'services/constants/types';
import { DATES_ARRAY } from 'services/constants/dates';
import { ActionSpeedDial } from './ActionSpeedDial';
import { COLORS } from 'services/constants/colors';

export type TEasTableHeaderProps<T> = {
	tableStructure: TTableStructure<T>[];
};

export const EasTableHeader = <T,>({ tableStructure }: TEasTableHeaderProps<T>) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);

	const filtersArray: TFilters = tableStructure.reduce((acc, item) => {
		if (navigateParams[item.selector as keyof typeof navigateParams])
			acc.push({
				fieldName: item.selector as string,
				value: navigateParams[item.selector as string] as string,
				operator: '=',
			});
		return acc;
	}, [] as TFilters);

	const filterMatrix = tableStructure?.map((item, index) => {
		if (navigateParams[`filter${index}`]) return navigateParams[`filter${index}`] as string;
		return '';
	});

	const handleSort = (index: number, direction: ESortDirection) => {
		const newFilters = [...filtersArray];

		navigate(
			`${location.pathname}?${queryString.stringify({
				...navigateParams,
				sortIndex: index,
				sortDirection: direction,
				page: 1,
			})}`,
		);

		const timestampIndex = newFilters.findIndex?.((item) => item?.fieldName === 'timestamp');
		if (timestampIndex > -1) {
			const dates = DATES_ARRAY.find((item) => item.id === newFilters?.[timestampIndex]?.value);
			if (dates?.from) {
				newFilters.splice(timestampIndex, 1);
				newFilters.push({
					fieldName: 'timestamp',
					value: dates.from,
					operator: '>=',
				});
				if (dates?.to) {
					newFilters.push({
						fieldName: 'timestamp',
						value: dates.to,
						operator: '<=',
					});
				}
			}
		}

		const schemesIndex = newFilters.findIndex?.((item) => item?.fieldName === 'schemes');
		if (schemesIndex > -1) {
			const schemesValue = newFilters?.[schemesIndex]?.value as string;
			newFilters.splice(schemesIndex, 1);
			newFilters.push({
				fieldName: 'schemes',
				value: `%${schemesValue}%`,
				operator: 'like',
			});
		}

		const idIndex = newFilters.findIndex?.(
			(item) => item?.fieldName === 'external_order_id' || item?.fieldName === 'externalOrderId',
		);
		if (idIndex > -1) {
			const idValue = newFilters?.[idIndex]?.value as string;
			const fieldName = newFilters[idIndex].fieldName;
			newFilters.splice(idIndex, 1);
			newFilters.push({
				fieldName,
				value: `%${idValue}%`,
				operator: 'like',
			});
		}

		// dispatch(
		// 	getConfirmedOrdersRequest({
		// 		filter: newFilters,
		// 		orderBy: direction
		// 			? [`${tableStructure[index].selector} ${direction as string}`]
		// 			: ['id desc'],
		// 		page: {
		// 			from: 0,
		// 			size: PAGE_SIZE,
		// 		},
		// 		tableName: 'dashboard_confirmed_orders',
		// 	}),
		// );
	};

	const sort =
		navigateParams.sortIndex && navigateParams.sortDirection
			? {
					index: Number(navigateParams.sortIndex ?? 0),
					direction: (navigateParams.sortDirection ?? ESortDirection.ASC) as ESortDirection,
				}
			: null;

	return (
		<TableRow
			sx={{
				borderTop: `1px solid ${COLORS.PRIMARY}70`,
				// backgroundColor: COLORS.BACKGROUND_SECONDARY,
			}}
		>
			{/* <StyledTableCell
				key={nanoid()}
				style={{
					padding: 0,
					backgroundColor: '#006293',
				}}
			>
				<Checkbox
					checked={checkedAll}
					indeterminate={indeterminate}
					onChange={(e) => {
						e.stopPropagation();
						setCheckAll(e.target.checked);
					}}
					onClick={(e) => {
						e.stopPropagation();
					}}
					sx={{
						color: 'white',
						'&.Mui-checked': {
							color: 'white',
						},
						'&.MuiCheckbox-indeterminate': {
							color: 'white',
						},
					}}
					// onChange={handleChange}
					// inputProps={{ 'aria-label': 'primary checkbox' }}
				/>
			</StyledTableCell> */}

			{tableStructure?.map((filter, index: number) => {
				if (filter.show === false) return null;
				if (filter?.noName)
					return (
						<StyledTableCell
							key={nanoid()}
							style={{ padding: 0, backgroundColor: COLORS.BACKGROUND_SECONDARY }}
						/>
					);
				return (
					<StyledTableCell
						key={nanoid()}
						align={filter?.align ?? 'center'}
						colSpan={filter.colSpan}
						style={{
							padding: '0px 5px 0px 5px',
							verticalAlign: 'top',
							backgroundColor:
								sort?.index === index
									? COLORS.BACKGROUND_SECONDARY
									: filterMatrix?.[index]
										? COLORS.BACKGROUND_SECONDARY
										: COLORS.BACKGROUND_SECONDARY,
						}}
					>
						<Box
							sx={{
								display: 'inline-flex',
								alignItems: 'start',
								justifyContent: 'start',
								flexDirection: 'row',
							}}
						>
							<Box
								sx={{
									display: 'block',
									m: '4px 0 4px 0',
									lineHeight: '1.4',
									textAlign: 'left',
									fontSize: '0.75rem',
									whiteSpace: 'pre-line',
									color: COLORS.PRIMARY,
								}}
							>
								{filter.name}
							</Box>
							{filter.sortable && (
								<Box
									sx={{
										display: 'inline-flex',
										alignItems: 'center',
										justifyContent: 'center',
										flexDirection: 'column',
										ml: '2px',
									}}
								>
									<KeyboardArrowUpIcon
										sx={{
											display: 'inline',
											mb: '-5px',
											mt: '0px',
											color: COLORS.PRIMARY,
											fontSize: 18,
											opacity:
												sort?.index === index && sort?.direction === ESortDirection.ASC ? 1 : 0.3,
										}}
										fontSize="small"
									/>
									<KeyboardArrowDownIcon
										sx={{
											display: 'inline',
											mt: '-5px',
											color: COLORS.PRIMARY,
											fontSize: 18,
											opacity:
												sort?.index === index && sort?.direction === ESortDirection.DESC ? 1 : 0.3,
										}}
										fontSize="small"
									/>
									<ActionSpeedDial
										sortable={filter.sortable}
										onChangeSort={(direction: ESortDirection) => handleSort(index, direction)}
										sortDirection={sort?.index === index ? sort?.direction : undefined}
									/>
								</Box>
							)}
						</Box>
					</StyledTableCell>
				);
			})}
		</TableRow>
	);
};
